import React, { useEffect, useRef, useState } from 'react';
import './Dropdown.css';

export interface DropdownOption<T = string> {
    value: T;
    label: string;
    icon?: React.ReactNode;
    color?: string;
}

export interface DropdownProps {
    options: DropdownOption[];
    onSelect: (option: DropdownOption | null) => void;
    placeholder?: string;
    selectedValue?: string | null;
    disabled?: boolean;
    className?: string;
    fullWidth?: boolean;
}

const StatusDot: React.FC<{ color: string }> = ({ color }) => (
    <span className="status-dot" style={{ backgroundColor: color }} />
);

const Dropdown: React.FC<DropdownProps> = ({
    options,
    onSelect,
    placeholder = 'Select an option',
    selectedValue = null,
    disabled = false,
    className = '',
    fullWidth = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const selected = options.find(o => o.value === selectedValue) ?? null;

    const handleSelect = (option: DropdownOption) => {
        onSelect(option);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect(null);
    };

    useEffect(() => {
        const onOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isOpen && (e.key === 'Escape' || e.key === 'Tab')) setIsOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen]);

    return (
        <div
            ref={ref}
            className={`dropdown-container ${className} ${disabled ? 'disabled' : ''} ${fullWidth ? 'full-width' : ''}`}
        >
            <button
                type="button"
                className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={() => !disabled && setIsOpen(v => !v)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="trigger-content">
                    {selected?.color && <StatusDot color={selected.color} />}
                    {selected?.icon && <span className="trigger-icon">{selected.icon}</span>}
                    <span className="dropdown-value">
                        {selected ? selected.label : <span className="dropdown-placeholder">{placeholder}</span>}
                    </span>
                </span>

                <span className="dropdown-icons">
                    {selected && (
                        <button
                            type="button"
                            className="clear-button"
                            onClick={handleClear}
                            aria-label="Clear selection"
                        >
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <path d="M8 2L2 8M2 2L8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </button>
                    )}
                    <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                </span>
            </button>

            {isOpen && (
                <ul className="dropdown-menu" role="listbox">
                    {options.length === 0 ? (
                        <li className="dropdown-empty">Нет вариантов</li>
                    ) : (
                        options.map(option => (
                            <li
                                key={String(option.value)}
                                className={`dropdown-option ${selected?.value === option.value ? 'selected' : ''}`}
                                onClick={() => handleSelect(option)}
                                role="option"
                                aria-selected={selected?.value === option.value}
                            >
                                {option.color && <StatusDot color={option.color} />}
                                {option.icon && <span className="option-icon">{option.icon}</span>}
                                <span className="option-label">{option.label}</span>
                                {selected?.value === option.value && (
                                    <svg className="option-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M11.5 3.5L5.5 10.5L2.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};

export default Dropdown;
